import {
  Badge,
  Button,
  Checkbox,
  ConditionalRendering,
  Loader,
  Modal,
  Switch,
  Text,
  Textarea,
  TextInput,
  UnstyledButton
} from "@marketplace/ui";
import clsx from "clsx";
import { useEffect } from "react";
import { FormProvider, useFieldArray } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { MdAdd, MdOutlineDeleteForever } from "react-icons/md";

import { useSetupAutomationNotifyForm } from "../../hooks/forms/use-setup-automation-notify-form";

export const AutomationSetupNotifyModal = () => {
  const { t } = useTranslation("product", {
    keyPrefix: "automation.notifyModal"
  });

  const { formMethods, onSubmit, handleClose, modalState, isPending } =
    useSetupAutomationNotifyForm();

  const { control, watch, setValue } = formMethods;
  const { fields, append, remove } = useFieldArray({
    control,
    name: "custom_notify"
  });
  const activeCustom = watch("active_custom");
  const disabled = isPending;

  useEffect(() => {
    if (activeCustom && fields.length === 0) {
      append({
        type: "",
        subject: "",
        to_list: "",
        cc_list: "",
        template: "",
        metadata: "{}",
        is_active: true
      });
    }
  }, [activeCustom, fields.length, append]);

  type RoleId = "is_notify_buyer" | "is_notify_seller" | "is_notify_superadmin";
  const roles: Array<{
    id: RoleId;
    title: string;
    badge: string;
    desc: string;
  }> = [
    {
      id: "is_notify_buyer",
      title: t("buyerTitle", "Buyer"),
      badge: t("standardScope", "Standard Scope"),
      desc: t("buyerDesc")
    },
    {
      id: "is_notify_seller",
      title: t("sellerTitle", "Seller"),
      badge: t("standardScope", "Standard Scope"),
      desc: t("sellerDesc")
    },
    {
      id: "is_notify_superadmin",
      title: t("superadminTitle", "Superadmin"),
      badge: t("fullAccess", "Full Access"),
      desc: t("superadminDesc")
    }
  ];

  const handleLoadingState = () => {
    setTimeout(() => {}, 2000);
    if (isPending) {
      return true;
    }
    return false;
  };

  return (
    <Modal
      opened={modalState.visible}
      onClose={handleClose}
      title={<Text type="heading">{t("title", "Setup Notify Product")}</Text>}
      size={610}
      classNames={{
        header: "!p-6",
        body: "!p-6 !pt-0"
      }}
    >
      <FormProvider {...formMethods}>
        <form onSubmit={onSubmit} className="flex flex-col gap-3 relative">
          <ConditionalRendering condition={handleLoadingState()}>
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60 rounded-lg">
              <Loader />
            </div>
          </ConditionalRendering>
          <div className="flex flex-col gap-4">
            {roles.map((role) => (
              <div
                key={role.id}
                className="rounded-lg border border-solid border-mischka-400 overflow-hidden"
              >
                <div
                  className={clsx(
                    "p-5 flex items-center gap-6 transition-colors",
                    !disabled && "cursor-pointer hover:bg-gray-50"
                  )}
                  onClick={() => {
                    if (disabled) return;
                    setValue(role.id, !watch(role.id));
                  }}
                >
                  <Checkbox
                    mt={4}
                    checked={watch(role.id)}
                    onChange={() => {}}
                    disabled={disabled}
                    className="pointer-events-none"
                    classNames={{
                      root: "!mt-0"
                    }}
                  />
                  <div className="flex-1">
                    <Text
                      type="heading"
                      size="xs"
                      weight="bold"
                      className="text-fjord-grey-500 mb-1"
                    >
                      {role.title}
                    </Text>
                    <Text size="s" className="text-fjord-grey-400">
                      {role.desc}
                    </Text>
                  </div>
                  <Badge color="gray" variant="light" size="md">
                    {role.badge}
                  </Badge>
                </div>
              </div>
            ))}

            {/* Active Custom */}
            <div
              className={clsx(
                "rounded-lg border border-solid overflow-hidden transition-colors",
                activeCustom ? "border-noble-blue-500" : "border-mischka-400"
              )}
            >
              <div
                className={clsx(
                  "p-5 flex items-center gap-6 transition-colors",
                  !disabled && "cursor-pointer hover:bg-gray-50",
                  activeCustom &&
                    "border-solid border-0 border-b border-b-mischka-400"
                )}
                onClick={() => {
                  if (disabled) return;
                  setValue("active_custom", !activeCustom);
                }}
              >
                <Checkbox
                  checked={activeCustom}
                  onChange={() => {}}
                  disabled={disabled}
                  className="pointer-events-none"
                  classNames={{
                    root: "!mt-0"
                  }}
                />
                <div className="flex-1">
                  <Text
                    type="heading"
                    size="xs"
                    weight="bold"
                    className="text-fjord-grey-500 mb-1"
                  >
                    {t("activeCustomTitle", "Active Custom")}
                  </Text>
                  <Text size="s" className="text-fjord-grey-400">
                    {t("activeCustomDesc")}
                  </Text>
                </div>
                <Badge
                  classNames={{
                    root: "bg-noble-blue-500/10",
                    label: "text-noble-blue-500"
                  }}
                >
                  {t("flexible", "Flexible")}
                </Badge>
              </div>

              {/* Custom Notify Form Fields */}
              {activeCustom && (
                <div className="p-5">
                  <div className="flex flex-col gap-4">
                    {fields.map((field, index) => (
                      <div
                        key={field.id}
                        className="p-[14px] border border-solid border-mischka-400 rounded-lg bg-mischka-100 flex flex-col gap-y-6"
                      >
                        <div className="flex items-center justify-between gap-2">
                          <Text size="xl" className="text-fjord-grey-500">
                            Custom {index + 1}
                          </Text>
                          <hr className="flex-1 h-px border-0 border-solid bg-mischka-400" />
                          {fields.length > 1 && (
                            <UnstyledButton
                              onClick={() => {
                                if (disabled) return;
                                remove(index);
                              }}
                              disabled={disabled}
                              className="flex items-center gap-x-1 text-[#FF0606] hover:opacity-80 disabled:opacity-50 disabled:cursor-not-allowed"
                              title={t("deleteCustom")}
                            >
                              <Text size="xl">{t("deleteCustom")}</Text>
                              <MdOutlineDeleteForever className="h-6 w-6" />
                            </UnstyledButton>
                          )}
                        </div>

                        <TextInput
                          label={t("type", "Type")}
                          placeholder={t("placeholders.type", "Your type")}
                          value={watch(`custom_notify.${index}.type`)}
                          onChange={(e) =>
                            setValue(
                              `custom_notify.${index}.type`,
                              e.target.value
                            )
                          }
                          disabled={disabled}
                          required
                        />
                        <TextInput
                          label={t("subject", "Subject")}
                          placeholder={t(
                            "placeholders.subject",
                            "Your subject"
                          )}
                          value={watch(`custom_notify.${index}.subject`)}
                          onChange={(e) =>
                            setValue(
                              `custom_notify.${index}.subject`,
                              e.target.value
                            )
                          }
                          disabled={disabled}
                          required
                        />
                        <TextInput
                          label={t("toList", "To List")}
                          placeholder={t("placeholders.toList", "Your to list")}
                          value={watch(`custom_notify.${index}.to_list`)}
                          onChange={(e) =>
                            setValue(
                              `custom_notify.${index}.to_list`,
                              e.target.value
                            )
                          }
                          disabled={disabled}
                          required
                        />
                        <TextInput
                          label={t("ccList", "CC List")}
                          placeholder={t("placeholders.ccList", "Your cc list")}
                          value={watch(`custom_notify.${index}.cc_list`)}
                          onChange={(e) =>
                            setValue(
                              `custom_notify.${index}.cc_list`,
                              e.target.value
                            )
                          }
                          disabled={disabled}
                        />
                        <TextInput
                          label={t("template", "Template")}
                          placeholder={t(
                            "placeholders.template",
                            "Your template"
                          )}
                          value={watch(`custom_notify.${index}.template`)}
                          onChange={(e) =>
                            setValue(
                              `custom_notify.${index}.template`,
                              e.target.value
                            )
                          }
                          disabled={disabled}
                          required
                        />
                        <Textarea
                          label={t("metadata")}
                          rows={6}
                          placeholder={t(
                            "placeholders.metadata",
                            "Your metadata"
                          )}
                          value={watch(`custom_notify.${index}.metadata`)}
                          classNames={{ input: "h-[232px]" }}
                          onChange={(e) =>
                            setValue(
                              `custom_notify.${index}.metadata`,
                              e.target.value
                            )
                          }
                          disabled={disabled}
                        />
                        <Switch
                          label={t("isActive", "Is Active")}
                          checked={watch(`custom_notify.${index}.is_active`)}
                          onChange={(e) =>
                            setValue(
                              `custom_notify.${index}.is_active`,
                              e.currentTarget.checked
                            )
                          }
                          disabled={disabled}
                          classNames={{
                            label: "text-fjord-grey-500 text-p-default"
                          }}
                        />
                      </div>
                    ))}

                    <Button
                      fullWidth
                      variant="secondary"
                      size="sm"
                      classNames={{
                        label: "text-fjord-grey-500",
                        section: "text-fjord-grey-500"
                      }}
                      onClick={() => {
                        if (disabled) return;
                        append({
                          type: "",
                          subject: "",
                          to_list: "",
                          cc_list: "",
                          template: "",
                          metadata: "{}",
                          is_active: true
                        });
                      }}
                      disabled={disabled}
                      rightSection={<MdAdd size={14} />}
                    >
                      {t("addCustom")}
                    </Button>
                  </div>

                  {fields.length === 0 && (
                    <Text
                      size="s"
                      color="dimmed"
                      className="text-center py-4 italic"
                    >
                      {t("noCustomItems", "No custom notifications added yet.")}
                    </Text>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <Button
              variant="secondary"
              onClick={handleClose}
              disabled={isPending}
              className="w-[128px]"
            >
              {t("cancel", "Cancel")}
            </Button>
            <Button
              variant="primary"
              type="submit"
              loading={isPending}
              disabled={isPending}
            >
              {t("submit", "Apply Configuration")}
            </Button>
          </div>
        </form>
      </FormProvider>
    </Modal>
  );
};
